import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import useAnalytics from '../hooks/useAnalytics';

// Inline InquiryModal component
function InquiryModal({ isOpen, onClose, product, firmId }) {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    if (!isOpen || !product) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const response = await api.post('/public/inquiries', {
                firm_id: firmId,
                product_id: product.id,
                inquiry: formData
            });
            if (response.success) {
                setSuccess(true);
                setTimeout(() => {
                    onClose();
                    setSuccess(false);
                    setFormData({ name: '', email: '', phone: '', message: '' });
                }, 2000);
            }
        } catch (error) {
            console.error('Inquiry failed:', error);
            alert('Failed to send inquiry. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h3 className="text-2xl font-black text-slate-900">Request Quote</h3>
                        <p className="text-sm text-slate-500 mt-1">{product.name}</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {success ? (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                            </svg>
                        </div>
                        <h4 className="text-xl font-bold text-slate-900 mb-2">Inquiry Sent!</h4>
                        <p className="text-slate-600">The seller will contact you shortly.</p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Your Name *</label>
                            <input
                                required
                                type="text"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                placeholder="John Doe"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Email (Optional)</label>
                            <input
                                type="email"
                                value={formData.email}
                                onChange={e => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                placeholder="you@example.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number *</label>
                            <div className="flex">
                                <span className="inline-flex items-center px-4 rounded-l-xl border border-r-0 border-slate-300 bg-slate-50 text-slate-600 text-sm font-semibold">
                                    +91
                                </span>
                                <input
                                    required
                                    type="tel"
                                    pattern="[0-9]{10}"
                                    maxLength="10"
                                    value={formData.phone}
                                    onChange={e => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                                    className="flex-1 px-4 py-3 border border-slate-300 rounded-r-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                                    placeholder="9876543210"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Message</label>
                            <textarea
                                rows="3"
                                value={formData.message}
                                onChange={e => setFormData({ ...formData, message: e.target.value })}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none transition-all"
                                placeholder="I'm interested in this product..."
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {submitting ? 'Sending...' : 'Send Inquiry'}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}

function StoreFront() {
    const { slug } = useParams();
    const [storeData, setStoreData] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [activeView, setActiveView] = useState('products'); // 'products' OR 'about'

    const { logEvent } = useAnalytics(storeData?.firm?.id);

    useEffect(() => {
        loadStore();
    }, [slug, selectedCategory]);

    const loadStore = async () => {
        try {
            setLoading(true);
            const params = selectedCategory ? { category_id: selectedCategory } : {};
            const response = await api.get(`/public/store/firm/${slug}`, params);

            if (response.success) {
                setStoreData(response.data);
            } else {
                setError('Store not found');
            }
        } catch (err) {
            console.error('Error loading store:', err);
            setError('Store not found or unavailable');
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = storeData?.products?.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600 font-medium">Loading store...</p>
                </div>
            </div>
        );
    }

    if (error || !storeData) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex flex-col items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full text-center">
                    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6 text-red-500">
                        <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="text-3xl font-bold text-slate-800 mb-3">Store Not Found</h2>
                    <p className="text-slate-600 mb-8 text-lg">The store you are looking for does not exist or has been disabled.</p>
                </div>
            </div>
        );
    }

    const { firm, products, categories } = storeData;

    return (
        <div className={`min-h-screen ${firm.store_theme === 'dark' ? 'bg-slate-900 text-white' : 'bg-gradient-to-br from-slate-50 via-white to-slate-50'}`}>
            {/* Hero Header */}
            <header className="relative bg-slate-900 text-white overflow-hidden min-h-[400px]">
                {/* Banner Image */}
                {firm.banner_url ? (
                    <div className="absolute inset-0 z-0">
                        <img src={firm.banner_url} alt="Banner" className="w-full h-full object-cover opacity-60" />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent"></div>
                    </div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 opacity-90 z-0">
                        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yIDItNCA0LTRzNCAxIDYgM2g0VjIzYy0yIDItNC00LTYtNHMtNCAxLTYgM3YxMnoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-20"></div>
                    </div>
                )}

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 relative z-10">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex-1 text-center md:text-left">
                            <div className="flex flex-col md:flex-row items-center gap-6 mb-6">
                                {firm.logo_url && (
                                    <div className="w-24 h-24 sm:w-32 sm:h-32 bg-white rounded-3xl p-2 shadow-2xl flex-shrink-0">
                                        <img src={firm.logo_url} alt="Logo" className="w-full h-full object-contain rounded-2xl" />
                                    </div>
                                )}
                                <div>
                                    <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-md rounded-full text-xs font-bold uppercase tracking-widest mb-3">
                                        {(firm.industry || 'Online Store').replace(/_/g, ' ')}
                                    </div>
                                    <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black mb-4 leading-tight tracking-tight">
                                        {firm.store_name || firm.name}
                                    </h1>
                                </div>
                            </div>
                            {firm.store_description && (
                                <p className="text-lg sm:text-xl text-white/90 max-w-2xl leading-relaxed mx-auto md:mx-0">
                                    {firm.store_description}
                                </p>
                            )}
                        </div>
                        <div className="flex flex-col gap-4">
                            {firm.whatsapp_number && (
                                <a
                                    href={`https://wa.me/${firm.whatsapp_number}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-3 bg-green-500 text-white px-8 py-4 rounded-2xl hover:bg-green-600 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 font-bold text-lg whitespace-nowrap"
                                    onClick={() => logEvent('click', { element: 'whatsapp_button' })}
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                    WhatsApp Now
                                </a>
                            )}
                            {firm.google_maps_link && (
                                <a
                                    href={firm.google_maps_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-2xl hover:bg-white/20 transition-all duration-300 font-bold"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Locate Store
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* Quick View Switcher */}
            <div className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-center sm:justify-start gap-8">
                        {['products', 'about'].map(view => (
                            <button
                                key={view}
                                onClick={() => setActiveView(view)}
                                className={`py-5 text-sm font-black uppercase tracking-widest transition-all relative ${activeView === view ? 'text-indigo-600' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {view}
                                {activeView === view && (
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-600 rounded-t-full"></div>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            {activeView === 'products' ? (
            <>
            <div className={`z-40 ${firm.store_theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white/95'} border-b border-slate-200 shadow-sm`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    {/* Search Bar */}
                    <div className="mb-4">
                        <div className="relative max-w-2xl mx-auto">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="block w-full pl-12 pr-4 py-3 border border-slate-300 rounded-xl leading-5 bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
                            />
                        </div>
                    </div>

                    {/* Category Pills */}
                    {categories.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                            <button
                                onClick={() => setSelectedCategory(null)}
                                className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 ${!selectedCategory
                                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-105'
                                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                            >
                                All Products
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    onClick={() => setSelectedCategory(cat.id)}
                                    className={`px-5 py-2.5 rounded-full text-sm font-bold whitespace-nowrap transition-all duration-200 ${selectedCategory === cat.id
                                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200 scale-105'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                        }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Product Grid */}
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {filteredProducts.length === 0 ? (
                    <div className="text-center py-20">
                        <div className="w-24 h-24 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-400">
                            <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                            </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-2">No products found</h3>
                        <p className="text-slate-500 text-lg">Try a different search term or category.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:gap-8">
                        {filteredProducts.map(product => (
                            <Link
                                key={product.id}
                                to={`/store/firm/${firm.slug}/product/${product.id}`}
                                className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden border border-slate-100 transform hover:-translate-y-2"
                                onClick={() => logEvent('click', { element: 'product_card', product_id: product.id })}
                            >
                                <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                                    {product.images?.[0] ? (
                                        <img
                                            src={product.images[0]}
                                            alt={product.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                                            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    )}
                                    {!product.in_stock && (
                                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                            <span className="px-4 py-2 bg-red-500 text-white text-sm font-bold rounded-full">
                                                Out of Stock
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="p-5">
                                    {product.category && (
                                        <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider mb-2">
                                            {product.category}
                                        </div>
                                    )}
                                    <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-2 group-hover:text-indigo-600 transition-colors" title={product.name}>
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center justify-between mt-4">
                                        <span className="text-2xl font-black text-indigo-600">
                                            ₹{parseFloat(product.selling_price).toLocaleString()}
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setSelectedProduct(product);
                                            setModalOpen(true);
                                            logEvent('click', { element: 'quick_quote', product_id: product.id });
                                        }}
                                        className="mt-3 w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-2.5 rounded-xl font-bold text-sm hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md"
                                    >
                                        Quick Quote
                                    </button>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </main>
            </>
            ) : (
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
                        {/* Company Story */}
                        <div className="lg:col-span-2 space-y-12">
                            <section>
                                <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tight flex items-center gap-4">
                                    <span className="w-1.5 h-12 bg-indigo-600 rounded-full"></span>
                                    Our Story
                                </h2>
                                <div className="text-xl text-slate-600 leading-relaxed font-medium whitespace-pre-line bg-slate-50/50 p-8 rounded-3xl border border-slate-100 shadow-inner">
                                    {firm.store_description || `${firm.name} has been serving quality products since its inception. We pride ourselves on customer satisfaction and premium service.`}
                                </div>
                            </section>

                            {firm.settings?.branch_details && (
                                <section>
                                    <h2 className="text-3xl font-black text-slate-900 mb-6 tracking-tight flex items-center gap-4">
                                        <span className="w-1.5 h-8 bg-purple-600 rounded-full"></span>
                                        Branch Locations
                                    </h2>
                                    <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-xl shadow-slate-100/50 relative overflow-hidden group">
                                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-purple-50 rounded-full blur-3xl opacity-50 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="relative z-10 text-lg text-slate-700 font-bold whitespace-pre-line leading-loose">
                                            {firm.settings.branch_details}
                                        </div>
                                    </div>
                                </section>
                            )}
                        </div>

                        {/* Social & Contact Card */}
                        <div className="space-y-8">
                            <div className="bg-slate-900 rounded-[3rem] p-8 text-white shadow-2xl relative overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-transparent"></div>
                                <h3 className="text-2xl font-black mb-8 relative z-10">Connect With Us</h3>
                                
                                <div className="grid grid-cols-1 gap-4 relative z-10">
                                    {Object.entries(firm.settings?.social_links || {}).map(([platform, url]: any) => {
                                        if (!url) return null;
                                        const icons: any = {
                                            instagram: '📸',
                                            facebook: '🔵',
                                            twitter: '🐦',
                                            linkedin: '💼',
                                            youtube: '🎥'
                                        };
                                        return (
                                            <a 
                                                key={platform}
                                                href={url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-4 p-4 bg-white/10 hover:bg-white/20 rounded-2xl transition-all border border-white/10 group active:scale-95"
                                            >
                                                <span className="text-2xl">{icons[platform] || '🌐'}</span>
                                                <span className="font-bold capitalize group-hover:translate-x-1 transition-transform">{platform}</span>
                                            </a>
                                        );
                                    })}
                                </div>

                                <div className="mt-12 pt-8 border-t border-white/10 relative z-10">
                                    <p className="text-indigo-300 text-[10px] font-black uppercase tracking-widest mb-4">Direct Contact</p>
                                    <div className="space-y-4">
                                        {firm.contact_email && <div className="flex items-center gap-3 text-sm font-bold opacity-80 hover:opacity-100 transition-opacity"><span className="text-xl">📧</span> {firm.contact_email}</div>}
                                        {firm.contact_phone && <div className="flex items-center gap-3 text-sm font-bold opacity-80 hover:opacity-100 transition-opacity"><span className="text-xl">📞</span> {firm.contact_phone}</div>}
                                    </div>
                                </div>
                            </div>
                            
                            {firm.google_maps_link && (
                                <a 
                                    href={firm.google_maps_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block p-8 bg-white border border-slate-200 rounded-[2.5rem] shadow-lg hover:shadow-2xl transition-all group overflow-hidden relative"
                                >
                                    <div className="absolute inset-0 bg-[url('https://maps.googleapis.com/maps/api/staticmap?center=20,77&zoom=4&size=400x400&sensor=false')] opacity-10 group-hover:scale-110 transition-transform duration-[2s]"></div>
                                    <div className="relative z-10">
                                        <div className="w-12 h-12 bg-indigo-600 text-white rounded-2xl flex items-center justify-center text-xl shadow-lg shadow-indigo-200 mb-6 group-hover:rotate-12 transition-transform">📍</div>
                                        <h4 className="text-xl font-black text-slate-900 mb-2">Visit Physical Store</h4>
                                        <p className="text-sm font-bold text-slate-400">View on Google Maps ↗</p>
                                    </div>
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Inquiry Modal */}
            {selectedProduct && (
                <InquiryModal
                    isOpen={modalOpen}
                    onClose={() => {
                        setModalOpen(false);
                        setSelectedProduct(null);
                    }}
                    product={selectedProduct}
                    firmId={firm.id}
                />
            )}

            {/* Footer */}
            <footer className="bg-slate-900 text-white mt-16 pt-16 pb-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
                        <div>
                            <h3 className="text-2xl font-black mb-6">{firm.store_name || firm.name}</h3>
                            <p className="text-slate-400 leading-relaxed mb-6">
                                {firm.store_description || 'Your one-stop shop for quality products and exceptional service.'}
                            </p>
                        </div>
                        <div>
                            <h4 className="text-lg font-bold mb-6 text-indigo-400">Contact Us</h4>
                            <ul className="space-y-4 text-slate-400">
                                {firm.contact_email && (
                                    <li className="flex items-center gap-3">
                                        <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <a href={`mailto:${firm.contact_email}`} className="hover:text-white transition-colors">{firm.contact_email}</a>
                                    </li>
                                )}
                                {firm.contact_phone && (
                                    <li className="flex items-center gap-3">
                                        <svg className="w-5 h-5 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href={`tel:${firm.contact_phone}`} className="hover:text-white transition-colors">{firm.contact_phone}</a>
                                    </li>
                                )}
                                {firm.whatsapp_number && (
                                    <li className="flex items-center gap-3">
                                        <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                        </svg>
                                        <a href={`https://wa.me/${firm.whatsapp_number}`} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{firm.whatsapp_number}</a>
                                    </li>
                                )}
                            </ul>
                        </div>
                        <div>
                            <h4 className="text-lg font-bold mb-6 text-indigo-400">Quick Links</h4>
                            <ul className="space-y-4 text-slate-400">
                                <li><a href="#" className="hover:text-white transition-colors">Home</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Products</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                                {firm.google_maps_link && (
                                    <li><a href={firm.google_maps_link} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Locate Store</a></li>
                                )}
                            </ul>
                        </div>
                    </div>
                    <div className="pt-8 border-t border-slate-800 text-center text-slate-500 text-sm">
                        <p>© {new Date().getFullYear()} {firm.name}. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}

export default StoreFront;
